import { useEffect, useState, useRef, useMemo } from "react";
import { PlatformIcon } from "@/components/PlatformIcon";

/* ─── Smooth number animator ─── */
function useAnimatedNumber(target: number, duration = 2800, delay = 400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const startTime = performance.now() + delay;
    let raf: number;
    const step = (now: number) => {
      const elapsed = now - startTime;
      if (elapsed < 0) { raf = requestAnimationFrame(step); return; }
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, delay]);
  return value;
}

/* ─── Sparkline SVG ─── */
function Sparkline({ color, delay = 0 }: { color: string; delay?: number }) {
  const pathRef = useRef<SVGPathElement>(null);
  const [drawn, setDrawn] = useState(false);

  const d = useMemo(() => {
    const pts: [number, number][] = [];
    for (let i = 0; i <= 16; i++) {
      const x = (i / 16) * 120;
      const base = 28 - (i / 16) * 20;
      const wobble = Math.sin(i * 1.4 + delay) * 4;
      pts.push([x, base + wobble]);
    }
    let path = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) {
      const [px, py] = pts[i - 1];
      const [cx, cy] = pts[i];
      const cpx = px + (cx - px) * 0.5;
      path += ` C ${cpx} ${py} ${cpx} ${cy} ${cx} ${cy}`;
    }
    return path;
  }, [delay]);

  useEffect(() => {
    const el = pathRef.current;
    if (!el) return;
    const len = el.getTotalLength();
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    const t = setTimeout(() => {
      el.style.transition = "stroke-dashoffset 2s cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.strokeDashoffset = "0";
      setDrawn(true);
    }, delay * 1000 + 600);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <svg className="w-[120px] h-[32px]" viewBox="0 0 120 32" fill="none">
      <defs>
        <linearGradient id={`sg-${delay}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {drawn && (
        <path d={`${d} L 120 32 L 0 32 Z`} fill={`url(#sg-${delay})`} />
      )}
      <path ref={pathRef} d={d} stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ─── Main component ─── */
export function RevenueGrowthHero({ isDark }: { isDark: boolean }) {
  const gmv = useAnimatedNumber(284700, 2800, 600);
  const pctGrowth = useAnimatedNumber(32, 1800, 1600);
  const [reveal, setReveal] = useState([false, false, false]);

  useEffect(() => {
    const t1 = setTimeout(() => setReveal(r => [true, r[1], r[2]]), 300);
    const t2 = setTimeout(() => setReveal(r => [r[0], true, r[2]]), 700);
    const t3 = setTimeout(() => setReveal(r => [r[0], r[1], true]), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const greenColor = isDark ? "rgba(74, 222, 128, 0.7)" : "rgba(22, 163, 74, 0.6)";

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div className="relative w-[460px] h-[340px]">

        {/* ── Card 1: Main GMV (back, shifted up-left) ── */}
        <div
          className={`absolute top-0 left-0 w-[320px] rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden transition-all duration-700 ease-out ${
            reveal[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ zIndex: 1 }}
        >
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-medium">Total GMV</span>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-destructive/80">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                <span className="text-[7px] font-bold text-white uppercase tracking-wider">Live</span>
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-[32px] font-[600] tracking-[-0.03em] text-foreground leading-none tabular-nums">
                ${gmv.toLocaleString()}
              </span>
              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 border border-success/25 transition-all duration-500 ${
                reveal[1] ? "opacity-100 scale-100" : "opacity-0 scale-90"
              }`}>
                <svg className="h-3 w-3 text-success" viewBox="0 0 16 16" fill="none">
                  <path d="M3 12L8 4l5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-[13px] font-semibold text-success tabular-nums">+{pctGrowth}%</span>
              </div>
            </div>
          </div>
          <div className="px-5 pb-4">
            <Sparkline color={greenColor} delay={0} />
          </div>
          <div className="flex items-center gap-4 px-5 py-3 border-t border-border/40 bg-muted/20">
            <div className="flex items-center gap-1.5">
              <PlatformIcon type="shopee" size={13} />
              <span className="text-[10px] text-muted-foreground">Shopee</span>
              <span className="text-[10px] font-medium text-foreground">$168k</span>
            </div>
            <div className="h-3 w-px bg-border" />
            <div className="flex items-center gap-1.5">
              <PlatformIcon type="tiktok" size={13} />
              <span className="text-[10px] text-muted-foreground">TikTok</span>
              <span className="text-[10px] font-medium text-foreground">$116k</span>
            </div>
          </div>
        </div>

        {/* ── Card 2: Top hosts (middle layer, shifted right) ── */}
        <div
          className={`absolute top-[90px] right-0 w-[220px] rounded-xl border border-border/60 bg-card shadow-xl overflow-hidden transition-all duration-700 ease-out ${
            reveal[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ zIndex: 2 }}
        >
          <div className="px-4 py-3 border-b border-border/40">
            <span className="text-[10px] font-semibold text-foreground">Top Hosts</span>
          </div>
          {[
            { name: "Bella P.", gmv: "220M", pct: 122 },
            { name: "Sarah A.", gmv: "212M", pct: 118 },
            { name: "Dina K.", gmv: "198M", pct: 110 },
          ].map((h, i) => (
            <div key={i} className="flex items-center gap-2.5 px-4 py-2 border-b border-border/30 last:border-0">
              <span className={`text-[10px] font-bold w-4 ${i === 0 ? "text-warning" : "text-muted-foreground"}`}>
                {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}
              </span>
              <span className="text-[11px] font-medium text-foreground flex-1 truncate">{h.name}</span>
              <div className="text-right">
                <span className="text-[10px] font-semibold text-foreground">IDR {h.gmv}</span>
                <span className="text-[8px] text-success ml-1">{h.pct}%</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Card 3: Session stats (front, bottom-left) ── */}
        <div
          className={`absolute bottom-0 left-[20px] w-[260px] rounded-xl border border-border/60 bg-card shadow-2xl overflow-hidden transition-all duration-700 ease-out ${
            reveal[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ zIndex: 3 }}
        >
          <div className="px-4 py-3 border-b border-border/40">
            <span className="text-[10px] font-semibold text-foreground">Live Session</span>
          </div>
          <div className="px-4 py-3 grid grid-cols-3 gap-3">
            {[
              { label: "Viewers", value: "4.2k", trend: "+18%" },
              { label: "Conv. Rate", value: "6.8%", trend: "+2.1%" },
              { label: "Avg. Order", value: "$47", trend: "+$6" },
            ].map((s, i) => (
              <div key={i}>
                <span className="text-[8px] uppercase tracking-wider text-muted-foreground">{s.label}</span>
                <div className="text-[16px] font-semibold text-foreground leading-tight mt-0.5">{s.value}</div>
                <span className="text-[9px] text-success font-medium">{s.trend}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
